import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { fetchWikiTree } from '../api/client'
import Breadcrumbs from '../components/Breadcrumbs'
import type { WikiTreeShelf } from '../types/wiki'
import { Book, Loader2, AlertCircle } from 'lucide-react'

export default function ShelfPage() {
  const { slug = '' } = useParams()
  const [shelf, setShelf] = useState<WikiTreeShelf | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    void fetchWikiTree()
      .then((tree) => {
        if (cancelled) return
        const found = tree.shelves.find((s) => s.slug === slug)
        if (!found) {
          setErr('Shelf not found')
          setShelf(null)
        } else {
          setShelf(found)
        }
      })
      .catch(() => {
        if (!cancelled) setErr('Wiki not available')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading shelf…
      </div>
    )
  }

  if (err || !shelf) {
    return (
      <div className="flex items-center gap-2 text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg p-4 m-4">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <span>{err || 'Shelf not found'}</span>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8">
      <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: shelf.name }]} />
      <h1 className="text-3xl font-bold text-gray-100 mb-2">{shelf.name}</h1>
      {shelf.description && <p className="text-gray-400 mb-8">{shelf.description}</p>}

      <h2 className="text-lg font-semibold text-gray-200 mb-4">Books</h2>
      {shelf.books.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500 border border-dashed border-gray-800 rounded-xl">
          <Book className="w-10 h-10 mb-2 opacity-40" />
          <p className="text-sm">No books in this shelf yet.</p>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {shelf.books.map((book) => (
          <Link
            key={book.id}
            to={`/b/${book.slug}`}
            className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5 hover:border-primary-700/50 transition-colors min-h-[44px]"
          >
            <div className="flex items-center gap-3">
              <Book className="w-5 h-5 text-amber-500/90 shrink-0" />
              <span className="font-medium text-gray-100">{book.name}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
